import React, { useEffect, useState, useMemo } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import { todayIso, fmtDateMedium, syncDateRange } from '../utils/dateUtils';
import { exportToPdf } from '../utils/exportToPdf';

function GstReports({ token, selectedPropertyId, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const colors = {
    primary: '#2c3e50', accent: '#3498db', success: '#27ae60', warning: '#f39c12',
    text: '#2c3e50', textLight: '#7f8c8d', background: '#ecf0f1',
    cardBg: '#ffffff', border: '#bdc3c7', headerBg: '#34495e', headerText: '#ffffff'
  };

  const [activeTab, setActiveTab] = useState('filing');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const today = todayIso();
  const firstOfMonth = today.substring(0, 8) + '01';
  const [dateRange, setDateRange] = useState({ from: firstOfMonth, to: today });
  const [moduleFilter, setModuleFilter] = useState('ALL');
  const [gstinSearch, setGstinSearch] = useState('');

  // Data states
  const [b2bRows, setB2bRows] = useState([]);
  const [b2cLargeRows, setB2cLargeRows] = useState([]);
  const [b2cSmallRows, setB2cSmallRows] = useState([]);
  const [hsnRows, setHsnRows] = useState([]);
  const [cdnRows, setCdnRows] = useState([]);
  const [gstr1Summary, setGstr1Summary] = useState(null);
  const [gstr3bSummary, setGstr3bSummary] = useState(null);
  const [moduleSummary, setModuleSummary] = useState([]);
  const [customerWise, setCustomerWise] = useState([]);
  const [filingData, setFilingData] = useState({ summary_by_seller: [], detail_by_seller_customer: [] });
  const [filingSellerFilter, setFilingSellerFilter] = useState('');

  const resolvePropertyId = () => String(selectedPropertyId || selectedProperty?.id || '').trim();
  const resolveChainId = () => String(selectedProperty?.chain_id || '').trim();
  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-property-id': resolvePropertyId(),
    ...(resolveChainId() ? { 'x-chain-id': resolveChainId() } : {})
  });

  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtCur = (n) => Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

  const baseParams = () => `from_date=${dateRange.from}&to_date=${dateRange.to}&module=${moduleFilter}`;

  const fetchTabData = async () => {
    setLoading(true);
    setError('');
    try {
      const base = `/api/v1/gst/reports`;
      const hdrs = authHeaders();
      const params = baseParams();

      switch (activeTab) {
        case 'b2b': {
          const r = await fetch(`${base}/b2b?${params}${gstinSearch ? `&customer_gstin=${gstinSearch}` : ''}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setB2bRows(d);
          break;
        }
        case 'b2c-large': {
          const r = await fetch(`${base}/b2c-large?${params}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setB2cLargeRows(d);
          break;
        }
        case 'b2c-small': {
          const r = await fetch(`${base}/b2c-small?${params}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setB2cSmallRows(d);
          break;
        }
        case 'hsn': {
          const r = await fetch(`${base}/hsn-summary?${params}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setHsnRows(d);
          break;
        }
        case 'cdn': {
          const r = await fetch(`${base}/cdn?${params}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setCdnRows(d);
          break;
        }
        case 'gstr1': {
          const r = await fetch(`${base}/gstr1-summary?${params}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setGstr1Summary(d);
          break;
        }
        case 'gstr3b': {
          const r = await fetch(`${base}/gstr3b-summary?from_date=${dateRange.from}&to_date=${dateRange.to}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setGstr3bSummary(d);
          break;
        }
        case 'module': {
          const r = await fetch(`${base}/module-summary?from_date=${dateRange.from}&to_date=${dateRange.to}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setModuleSummary(d);
          break;
        }
        case 'customer': {
          const r = await fetch(`${base}/customer-wise?${params}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setCustomerWise(d);
          break;
        }
        case 'filing': {
          const sellerParam = filingSellerFilter ? `&seller_gstin=${filingSellerFilter}` : '';
          const r = await fetch(`${base}/filing?${params}${sellerParam}`, { headers: hdrs });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Failed');
          setFilingData(d);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && selectedPropertyId) fetchTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, selectedPropertyId]);

  // Summary cards from module data
  const summaryTotals = useMemo(() => {
    const rows = moduleSummary || [];
    return {
      invoices: rows.reduce((s, r) => s + Number(r.invoice_count || 0), 0),
      taxable: rows.reduce((s, r) => s + Number(r.taxable_amount || 0), 0),
      cgst: rows.reduce((s, r) => s + Number(r.cgst_collected || 0), 0),
      sgst: rows.reduce((s, r) => s + Number(r.sgst_collected || 0), 0),
      igst: rows.reduce((s, r) => s + Number(r.igst_collected || 0), 0),
      tax: rows.reduce((s, r) => s + Number(r.total_tax || 0), 0),
      total: rows.reduce((s, r) => s + Number(r.grand_total || 0), 0)
    };
  }, [moduleSummary]);

  const tabs = [
    { key: 'filing', label: 'GST Filing' },
    { key: 'b2b', label: 'B2B' },
    { key: 'b2c-large', label: 'B2C Large' },
    { key: 'b2c-small', label: 'B2C Small' },
    { key: 'hsn', label: 'HSN/SAC' },
    { key: 'cdn', label: 'Credit/Debit Notes' },
    { key: 'gstr1', label: 'GSTR-1' },
    { key: 'gstr3b', label: 'GSTR-3B' },
    { key: 'module', label: 'Module Summary' },
    { key: 'customer', label: 'Customer-Wise' }
  ];

  const exportCSV = (rows, filename) => {
    if (!rows || !rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}_${dateRange.from}_${dateRange.to}.csv`;
    a.click();
  };

  const getExportRows = () => {
    switch (activeTab) {
      case 'b2b': return b2bRows;
      case 'b2c-large': return b2cLargeRows;
      case 'b2c-small': return b2cSmallRows;
      case 'hsn': return hsnRows;
      case 'cdn': return cdnRows;
      case 'module': return moduleSummary;
      case 'customer': return customerWise;
      case 'filing': return filingData.detail_by_seller_customer || [];
      default: return [];
    }
  };

  // Per-tab PDF column metadata. Keeps the print export aligned with the on-screen grid
  // and avoids relying on window.print() (which clips wide tables).
  const buildPdfSections = () => {
    const propertyName = selectedProperty?.name || '';
    const titleByTab = {
      'b2b': 'GST B2B Invoices',
      'b2c-large': 'GST B2C Large Invoices',
      'b2c-small': 'GST B2C Small Invoices',
      'hsn': 'GST HSN / SAC Summary',
      'cdn': 'GST Credit / Debit Notes',
      'gstr1': 'GSTR-1 Summary',
      'gstr3b': 'GSTR-3B Summary',
      'module': 'GST Module-wise Summary',
      'customer': 'GST Customer-wise Summary',
      'filing': 'GST Filing Report'
    };
    const title = titleByTab[activeTab] || 'GST Report';
    const sections = [];
    if (activeTab === 'b2b') {
      sections.push({
        columns: [
          { header: 'GSTIN', dataKey: 'customer_gstin' },
          { header: 'Receiver', dataKey: 'customer_name' },
          { header: 'Invoice No', dataKey: 'invoice_no' },
          { header: 'Date', dataKey: '_invoice_date' },
          { header: 'Module', dataKey: 'source_module' },
          { header: 'POS', dataKey: 'place_of_supply' },
          { header: 'Taxable', dataKey: '_taxable', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
          { header: 'Total', dataKey: '_total', halign: 'right' },
        ],
        rows: b2bRows.map(r => ({
          ...r,
          _invoice_date: fmtDateMedium(r.invoice_date),
          _taxable: fmt(r.taxable_amount), _cgst: fmt(r.cgst_amount),
          _sgst: fmt(r.sgst_amount), _igst: fmt(r.igst_amount), _total: fmt(r.total_amount),
        })),
      });
    } else if (activeTab === 'b2c-large') {
      sections.push({
        columns: [
          { header: 'Place of Supply', dataKey: 'place_of_supply' },
          { header: 'Taxable Value', dataKey: '_taxable', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
          { header: 'Total Tax', dataKey: '_total_tax', halign: 'right' },
        ],
        rows: b2cLargeRows.map(r => ({
          ...r,
          _taxable: fmt(r.taxable_value), _igst: fmt(r.igst_amount), _total_tax: fmt(r.total_tax),
        })),
      });
    } else if (activeTab === 'b2c-small') {
      sections.push({
        columns: [
          { header: 'Place of Supply', dataKey: 'place_of_supply' },
          { header: 'Taxable Value', dataKey: '_taxable', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
        ],
        rows: b2cSmallRows.map(r => ({
          ...r,
          _taxable: fmt(r.taxable_value), _cgst: fmt(r.cgst_amount),
          _sgst: fmt(r.sgst_amount), _igst: fmt(r.igst_amount),
        })),
      });
    } else if (activeTab === 'hsn') {
      sections.push({
        columns: [
          { header: 'SAC/HSN', dataKey: 'sac_code' },
          { header: 'Qty', dataKey: 'total_quantity', halign: 'right' },
          { header: 'Taxable', dataKey: '_taxable', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
          { header: 'Total Tax', dataKey: '_total_tax', halign: 'right' },
        ],
        rows: hsnRows.map(r => ({
          ...r,
          _taxable: fmt(r.taxable_amount), _cgst: fmt(r.cgst_amount),
          _sgst: fmt(r.sgst_amount), _igst: fmt(r.igst_amount), _total_tax: fmt(r.total_tax),
        })),
      });
    } else if (activeTab === 'cdn') {
      const sampleKeys = cdnRows.length ? Object.keys(cdnRows[0]) : [];
      sections.push({
        columns: sampleKeys.map(k => ({ header: k, dataKey: k })),
        rows: cdnRows,
      });
    } else if (activeTab === 'module') {
      const sampleKeys = moduleSummary.length ? Object.keys(moduleSummary[0]) : [];
      sections.push({
        columns: sampleKeys.map(k => ({ header: k, dataKey: k })),
        rows: moduleSummary,
      });
    } else if (activeTab === 'customer') {
      const sampleKeys = customerWise.length ? Object.keys(customerWise[0]) : [];
      sections.push({
        columns: sampleKeys.map(k => ({ header: k, dataKey: k })),
        rows: customerWise,
      });
    } else if (activeTab === 'gstr1' || activeTab === 'gstr3b') {
      const summary = activeTab === 'gstr1' ? gstr1Summary : gstr3bSummary;
      if (summary && typeof summary === 'object') {
        sections.push({
          columns: [
            { header: 'Metric', dataKey: 'metric' },
            { header: 'Value', dataKey: 'value', halign: 'right' },
          ],
          rows: Object.entries(summary).map(([k, v]) => ({
            metric: k,
            value: typeof v === 'number' ? fmt(v) : String(v ?? ''),
          })),
        });
      }
    } else if (activeTab === 'filing') {
      const { summary_by_seller = [], detail_by_seller_customer = [] } = filingData || {};
      sections.push({
        heading: 'GST Payable — By Billing Profile (Seller GSTIN)',
        columns: [
          { header: 'Seller GSTIN', dataKey: 'seller_gstin' },
          { header: 'Entity', dataKey: 'seller_name' },
          { header: 'Modules', dataKey: 'modules' },
          { header: 'Invoices', dataKey: 'invoice_count', halign: 'right' },
          { header: 'B2B', dataKey: 'b2b_count', halign: 'right' },
          { header: 'B2C', dataKey: 'b2c_count', halign: 'right' },
          { header: 'Taxable', dataKey: '_taxable', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
          { header: 'Total Tax', dataKey: '_total_tax', halign: 'right' },
          { header: 'Total Value', dataKey: '_total_value', halign: 'right' },
        ],
        rows: summary_by_seller.map(r => ({
          ...r,
          _taxable: fmt(r.total_taxable), _cgst: fmt(r.total_cgst),
          _sgst: fmt(r.total_sgst), _igst: fmt(r.total_igst),
          _total_tax: fmt(r.total_tax), _total_value: fmt(r.total_value),
        })),
      });
      sections.push({
        heading: 'Invoice Detail — Seller GSTIN vs Customer GSTIN',
        columns: [
          { header: 'Seller GSTIN', dataKey: 'seller_gstin' },
          { header: 'Entity', dataKey: 'seller_name' },
          { header: 'Module', dataKey: 'source_module' },
          { header: 'Customer GSTIN', dataKey: 'customer_gstin' },
          { header: 'Customer Name', dataKey: 'customer_name' },
          { header: 'Invoices', dataKey: 'invoice_count', halign: 'right' },
          { header: 'Taxable', dataKey: '_taxable', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
          { header: 'Tax', dataKey: '_tax', halign: 'right' },
          { header: 'Total', dataKey: '_total', halign: 'right' },
        ],
        rows: detail_by_seller_customer.map(r => ({
          ...r,
          _taxable: fmt(r.taxable_amount), _cgst: fmt(r.cgst_amount),
          _sgst: fmt(r.sgst_amount), _igst: fmt(r.igst_amount),
          _tax: fmt(r.tax_amount), _total: fmt(r.total_amount),
        })),
      });
    }
    return { title, sections, propertyName };
  };

  const downloadPDF = () => {
    const { title, sections, propertyName } = buildPdfSections();
    if (!sections.length || sections.every(s => !s.rows || !s.rows.length)) return;
    exportToPdf({
      title,
      subtitle: propertyName ? `${propertyName} — ${dateRange.from} to ${dateRange.to}` : `${dateRange.from} to ${dateRange.to}`,
      meta: [`Generated: ${new Date().toLocaleString()}`],
      sections,
      orientation: 'landscape',
      filename: `gst_${activeTab}_${dateRange.from}_${dateRange.to}.pdf`,
      footerLeft: propertyName || '',
    });
  };

  // ── Styles ──
  const S = {
    container: { padding: isMobile ? '12px' : '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
    title: { fontSize: '22px', fontWeight: 700, color: colors.primary, margin: 0 },
    filtersRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' },
    input: { padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '14px' },
    select: { padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '14px', background: '#fff' },
    btn: { padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 },
    btnPrimary: { background: colors.accent, color: '#fff' },
    btnSuccess: { background: colors.success, color: '#fff' },
    tabBar: { display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '20px', borderBottom: `2px solid ${colors.border}`, paddingBottom: '4px' },
    tab: (active) => ({
      padding: '8px 16px', cursor: 'pointer', borderRadius: '6px 6px 0 0', fontSize: '13px', fontWeight: 600,
      background: active ? colors.accent : 'transparent', color: active ? '#fff' : colors.textLight,
      border: 'none', transition: 'all 0.2s'
    }),
    summaryBar: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' },
    card: { flex: '1 1 140px', background: colors.cardBg, borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', textAlign: 'center' },
    cardLabel: { fontSize: '11px', color: colors.textLight, textTransform: 'uppercase', letterSpacing: '0.5px' },
    cardValue: { fontSize: '18px', fontWeight: 700, color: colors.primary, marginTop: '4px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: { background: colors.headerBg, color: colors.headerText, padding: '10px 12px', textAlign: 'left', fontWeight: 600, position: 'sticky', top: 0 },
    thR: { background: colors.headerBg, color: colors.headerText, padding: '10px 12px', textAlign: 'right', fontWeight: 600, position: 'sticky', top: 0 },
    td: { padding: '8px 12px', borderBottom: `1px solid ${colors.border}`, color: colors.text },
    tdR: { padding: '8px 12px', borderBottom: `1px solid ${colors.border}`, color: colors.text, textAlign: 'right', fontFamily: 'monospace' },
    badge: (bg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: bg, color: '#fff' }),
    empty: { textAlign: 'center', padding: '40px', color: colors.textLight, fontSize: '14px' },
    tableWrap: { overflowX: 'auto', maxHeight: '70vh', border: `1px solid ${colors.border}`, borderRadius: '8px' }
  };

  const renderFiling = () => {
    const { summary_by_seller, detail_by_seller_customer } = filingData;
    if (!summary_by_seller?.length) return <div style={S.empty}>Click "Load Report" to generate GST Filing report for the selected period</div>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Summary by Seller GSTIN (each billing profile) */}
        <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', color: colors.primary, fontSize: '15px' }}>GST Payable — By Billing Profile (Seller GSTIN)</h3>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Seller GSTIN</th><th style={S.th}>Entity Name</th><th style={S.th}>Modules</th>
                  <th style={S.thR}>Invoices</th><th style={S.thR}>B2B</th><th style={S.thR}>B2C</th>
                  <th style={S.thR}>Taxable</th><th style={S.thR}>CGST</th><th style={S.thR}>SGST</th>
                  <th style={S.thR}>IGST</th><th style={S.thR}>Total Tax</th><th style={S.thR}>Total Value</th>
                </tr>
              </thead>
              <tbody>
                {summary_by_seller.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#f9fafb' : '#fff', cursor: 'pointer' }} onClick={() => setFilingSellerFilter(r.seller_gstin === filingSellerFilter ? '' : r.seller_gstin)}>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#1e40af' }}>{r.seller_gstin || '(Not Set)'}</td>
                    <td style={S.td}>{r.seller_name || '-'}</td>
                    <td style={S.td}>{r.modules}</td>
                    <td style={S.tdR}>{r.invoice_count}</td>
                    <td style={S.tdR}>{r.b2b_count}</td>
                    <td style={S.tdR}>{r.b2c_count}</td>
                    <td style={S.tdR}>{fmt(r.total_taxable)}</td>
                    <td style={S.tdR}>{fmt(r.total_cgst)}</td>
                    <td style={S.tdR}>{fmt(r.total_sgst)}</td>
                    <td style={S.tdR}>{fmt(r.total_igst)}</td>
                    <td style={{ ...S.tdR, fontWeight: 700, color: '#dc2626' }}>{fmt(r.total_tax)}</td>
                    <td style={{ ...S.tdR, fontWeight: 700 }}>{fmt(r.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textLight }}>Click a row to filter the detail below by that seller GSTIN.
            {filingSellerFilter && <span style={{ marginLeft: '8px', fontWeight: 600, color: '#1e40af' }}>Filtered: {filingSellerFilter} <button onClick={() => { setFilingSellerFilter(''); }} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>(clear)</button></span>}
          </div>
        </div>

        {/* Detail: Per Seller → Per Customer GSTIN breakdown */}
        <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', color: colors.primary, fontSize: '15px' }}>Invoice Detail — Seller GSTIN vs Customer GSTIN</h3>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Seller GSTIN</th><th style={S.th}>Entity</th><th style={S.th}>Module</th>
                  <th style={S.th}>Customer GSTIN</th><th style={S.th}>Customer Name</th>
                  <th style={S.thR}>Invoices</th><th style={S.thR}>Taxable</th>
                  <th style={S.thR}>CGST</th><th style={S.thR}>SGST</th><th style={S.thR}>IGST</th>
                  <th style={S.thR}>Tax</th><th style={S.thR}>Total</th>
                </tr>
              </thead>
              <tbody>
                {detail_by_seller_customer
                  .filter(r => !filingSellerFilter || r.seller_gstin === filingSellerFilter)
                  .map((r, i) => (
                  <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '11px' }}>{r.seller_gstin || '(Not Set)'}</td>
                    <td style={{ ...S.td, fontSize: '12px' }}>{r.seller_name || '-'}</td>
                    <td style={S.td}><span style={S.badge(r.source_module === 'PMS' ? '#3498db' : r.source_module === 'KOT' ? '#e67e22' : r.source_module === 'BAR' ? '#9b59b6' : '#16a085')}>{r.source_module}</span></td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '11px' }}>{r.customer_gstin === 'B2C' ? <span style={{ color: '#9ca3af' }}>B2C (No GSTIN)</span> : r.customer_gstin}</td>
                    <td style={S.td}>{r.customer_name || '-'}</td>
                    <td style={S.tdR}>{r.invoice_count}</td>
                    <td style={S.tdR}>{fmt(r.taxable_amount)}</td>
                    <td style={S.tdR}>{fmt(r.cgst_amount)}</td>
                    <td style={S.tdR}>{fmt(r.sgst_amount)}</td>
                    <td style={S.tdR}>{fmt(r.igst_amount)}</td>
                    <td style={S.tdR}>{fmt(r.tax_amount)}</td>
                    <td style={{ ...S.tdR, fontWeight: 600 }}>{fmt(r.total_amount)}</td>
                  </tr>
                ))}
                {!detail_by_seller_customer.length && <tr><td colSpan={12} style={S.empty}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderB2B = () => (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>GSTIN</th><th style={S.th}>Receiver</th><th style={S.th}>Invoice No</th>
            <th style={S.th}>Date</th><th style={S.th}>Module</th><th style={S.th}>POS</th>
            <th style={S.thR}>Taxable</th><th style={S.thR}>CGST</th><th style={S.thR}>SGST</th>
            <th style={S.thR}>IGST</th><th style={S.thR}>Total</th>
          </tr>
        </thead>
        <tbody>
          {b2bRows.map((r, i) => (
            <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
              <td style={S.td}><span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.customer_gstin}</span></td>
              <td style={S.td}>{r.customer_name || '-'}</td>
              <td style={S.td}>{r.invoice_no}</td>
              <td style={S.td}>{fmtDateMedium(r.invoice_date)}</td>
              <td style={S.td}><span style={S.badge(r.source_module === 'PMS' ? '#3498db' : r.source_module === 'KOT' ? '#e67e22' : r.source_module === 'BAR' ? '#9b59b6' : '#16a085')}>{r.source_module}</span></td>
              <td style={S.td}>{r.place_of_supply || '-'}</td>
              <td style={S.tdR}>{fmt(r.taxable_amount)}</td>
              <td style={S.tdR}>{fmt(r.cgst_amount)}</td>
              <td style={S.tdR}>{fmt(r.sgst_amount)}</td>
              <td style={S.tdR}>{fmt(r.igst_amount)}</td>
              <td style={S.tdR}><strong>{fmt(r.total_amount)}</strong></td>
            </tr>
          ))}
          {!b2bRows.length && <tr><td colSpan={11} style={S.empty}>No B2B invoices found for selected period</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderB2CLarge = () => (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr><th style={S.th}>Place of Supply</th><th style={S.thR}>Taxable Value</th><th style={S.thR}>IGST</th><th style={S.thR}>Total Tax</th></tr>
        </thead>
        <tbody>
          {b2cLargeRows.map((r, i) => (
            <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
              <td style={S.td}>{r.place_of_supply}</td>
              <td style={S.tdR}>{fmt(r.taxable_value)}</td>
              <td style={S.tdR}>{fmt(r.igst_amount)}</td>
              <td style={S.tdR}><strong>{fmt(r.total_tax)}</strong></td>
            </tr>
          ))}
          {!b2cLargeRows.length && <tr><td colSpan={4} style={S.empty}>No B2C Large invoices found</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderB2CSmall = () => (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr><th style={S.th}>Place of Supply</th><th style={S.thR}>Taxable Value</th><th style={S.thR}>CGST</th><th style={S.thR}>SGST</th><th style={S.thR}>IGST</th></tr>
        </thead>
        <tbody>
          {b2cSmallRows.map((r, i) => (
            <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
              <td style={S.td}>{r.place_of_supply}</td>
              <td style={S.tdR}>{fmt(r.taxable_value)}</td>
              <td style={S.tdR}>{fmt(r.cgst_amount)}</td>
              <td style={S.tdR}>{fmt(r.sgst_amount)}</td>
              <td style={S.tdR}>{fmt(r.igst_amount)}</td>
            </tr>
          ))}
          {!b2cSmallRows.length && <tr><td colSpan={5} style={S.empty}>No B2C Small invoices found</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderHSN = () => (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr><th style={S.th}>SAC/HSN Code</th><th style={S.thR}>Qty</th><th style={S.thR}>Taxable</th><th style={S.thR}>CGST</th><th style={S.thR}>SGST</th><th style={S.thR}>IGST</th><th style={S.thR}>Total Tax</th></tr>
        </thead>
        <tbody>
          {hsnRows.map((r, i) => (
            <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
              <td style={{ ...S.td, fontFamily: 'monospace' }}>{r.sac_code}</td>
              <td style={S.tdR}>{r.total_quantity}</td>
              <td style={S.tdR}>{fmt(r.total_taxable)}</td>
              <td style={S.tdR}>{fmt(r.total_cgst)}</td>
              <td style={S.tdR}>{fmt(r.total_sgst)}</td>
              <td style={S.tdR}>{fmt(r.total_igst)}</td>
              <td style={S.tdR}><strong>{fmt(r.total_tax)}</strong></td>
            </tr>
          ))}
          {!hsnRows.length && <tr><td colSpan={7} style={S.empty}>No HSN/SAC data found</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderCDN = () => (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Type</th><th style={S.th}>Note No</th><th style={S.th}>Date</th><th style={S.th}>Module</th>
            <th style={S.th}>Original Invoice</th><th style={S.th}>GSTIN</th><th style={S.th}>Customer</th>
            <th style={S.thR}>Taxable</th><th style={S.thR}>Tax</th><th style={S.thR}>Total</th>
          </tr>
        </thead>
        <tbody>
          {cdnRows.map((r, i) => (
            <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
              <td style={S.td}><span style={S.badge(r.note_type === 'CREDIT_NOTE' ? '#e74c3c' : '#f39c12')}>{r.note_type === 'CREDIT_NOTE' ? 'CN' : 'DN'}</span></td>
              <td style={S.td}>{r.note_no}</td>
              <td style={S.td}>{fmtDateMedium(r.note_date)}</td>
              <td style={S.td}>{r.source_module}</td>
              <td style={S.td}>{r.original_invoice_no}</td>
              <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '12px' }}>{r.customer_gstin || '-'}</td>
              <td style={S.td}>{r.customer_name || '-'}</td>
              <td style={S.tdR}>{fmt(r.taxable_amount)}</td>
              <td style={S.tdR}>{fmt(r.tax_amount)}</td>
              <td style={S.tdR}><strong>{fmt(r.total_amount)}</strong></td>
            </tr>
          ))}
          {!cdnRows.length && <tr><td colSpan={10} style={S.empty}>No credit/debit notes found</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderGSTR1 = () => {
    if (!gstr1Summary) return <div style={S.empty}>Click "Load Report" to generate GSTR-1 summary</div>;
    const { b2b, b2c, credit_debit_notes } = gstr1Summary;
    const cn = credit_debit_notes?.find(r => r.note_type === 'CREDIT_NOTE') || {};
    const dn = credit_debit_notes?.find(r => r.note_type === 'DEBIT_NOTE') || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h4 style={{ margin: '0 0 12px', color: colors.primary }}>Table 4 — B2B Invoices</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
            <div><span style={S.cardLabel}>Count</span><div style={S.cardValue}>{b2b?.count || 0}</div></div>
            <div><span style={S.cardLabel}>Taxable</span><div style={S.cardValue}>{fmtCur(b2b?.taxable)}</div></div>
            <div><span style={S.cardLabel}>CGST</span><div style={S.cardValue}>{fmtCur(b2b?.cgst)}</div></div>
            <div><span style={S.cardLabel}>SGST</span><div style={S.cardValue}>{fmtCur(b2b?.sgst)}</div></div>
            <div><span style={S.cardLabel}>IGST</span><div style={S.cardValue}>{fmtCur(b2b?.igst)}</div></div>
            <div><span style={S.cardLabel}>Total</span><div style={S.cardValue}>{fmtCur(b2b?.total)}</div></div>
          </div>
        </div>
        <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h4 style={{ margin: '0 0 12px', color: colors.primary }}>Table 7 — B2C Invoices</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
            <div><span style={S.cardLabel}>Count</span><div style={S.cardValue}>{b2c?.count || 0}</div></div>
            <div><span style={S.cardLabel}>Taxable</span><div style={S.cardValue}>{fmtCur(b2c?.taxable)}</div></div>
            <div><span style={S.cardLabel}>CGST</span><div style={S.cardValue}>{fmtCur(b2c?.cgst)}</div></div>
            <div><span style={S.cardLabel}>SGST</span><div style={S.cardValue}>{fmtCur(b2c?.sgst)}</div></div>
            <div><span style={S.cardLabel}>IGST</span><div style={S.cardValue}>{fmtCur(b2c?.igst)}</div></div>
            <div><span style={S.cardLabel}>Total</span><div style={S.cardValue}>{fmtCur(b2c?.total)}</div></div>
          </div>
        </div>
        <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h4 style={{ margin: '0 0 12px', color: colors.primary }}>Table 9 — Credit/Debit Notes</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <strong style={{ color: '#e74c3c' }}>Credit Notes</strong>
              <div style={{ marginTop: '8px' }}>Count: {cn.count || 0} | Taxable: {fmtCur(cn.taxable)} | Tax: {fmtCur((Number(cn.cgst)||0) + (Number(cn.sgst)||0) + (Number(cn.igst)||0))}</div>
            </div>
            <div>
              <strong style={{ color: '#f39c12' }}>Debit Notes</strong>
              <div style={{ marginTop: '8px' }}>Count: {dn.count || 0} | Taxable: {fmtCur(dn.taxable)} | Tax: {fmtCur((Number(dn.cgst)||0) + (Number(dn.sgst)||0) + (Number(dn.igst)||0))}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGSTR3B = () => {
    if (!gstr3bSummary) return <div style={S.empty}>Click "Load Report" to generate GSTR-3B summary</div>;
    const s31 = gstr3bSummary.section_3_1 || {};
    const s32 = gstr3bSummary.section_3_2 || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h4 style={{ margin: '0 0 12px', color: colors.primary }}>3.1(a) — Outward Taxable Supplies</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
            <div><span style={S.cardLabel}>Taxable Value</span><div style={S.cardValue}>{fmtCur(s31.taxable)}</div></div>
            <div><span style={S.cardLabel}>CGST</span><div style={S.cardValue}>{fmtCur(s31.cgst)}</div></div>
            <div><span style={S.cardLabel}>SGST</span><div style={S.cardValue}>{fmtCur(s31.sgst)}</div></div>
            <div><span style={S.cardLabel}>IGST</span><div style={S.cardValue}>{fmtCur(s31.igst)}</div></div>
            <div><span style={S.cardLabel}>Total Tax</span><div style={S.cardValue}>{fmtCur(s31.total_tax)}</div></div>
          </div>
        </div>
        {s32.length > 0 && (
          <div style={{ background: colors.cardBg, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <h4 style={{ margin: '0 0 12px', color: colors.primary }}>3.2 — Inter-State Supplies to Unregistered Persons</h4>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Place of Supply</th><th style={S.thR}>Taxable</th><th style={S.thR}>IGST</th></tr></thead>
              <tbody>
                {s32.map((r, i) => (
                  <tr key={i}><td style={S.td}>{r.place_of_supply}</td><td style={S.tdR}>{fmt(r.taxable)}</td><td style={S.tdR}>{fmt(r.igst)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderModuleSummary = () => (
    <div>
      <div style={S.summaryBar}>
        <div style={S.card}><div style={S.cardLabel}>Total Invoices</div><div style={S.cardValue}>{summaryTotals.invoices}</div></div>
        <div style={S.card}><div style={S.cardLabel}>Taxable</div><div style={S.cardValue}>{fmtCur(summaryTotals.taxable)}</div></div>
        <div style={S.card}><div style={S.cardLabel}>CGST</div><div style={S.cardValue}>{fmtCur(summaryTotals.cgst)}</div></div>
        <div style={S.card}><div style={S.cardLabel}>SGST</div><div style={S.cardValue}>{fmtCur(summaryTotals.sgst)}</div></div>
        <div style={S.card}><div style={S.cardLabel}>IGST</div><div style={S.cardValue}>{fmtCur(summaryTotals.igst)}</div></div>
        <div style={S.card}><div style={S.cardLabel}>Total Tax</div><div style={S.cardValue}>{fmtCur(summaryTotals.tax)}</div></div>
        <div style={{ ...S.card, background: '#eaf6ff' }}><div style={S.cardLabel}>Grand Total</div><div style={{ ...S.cardValue, color: colors.accent }}>{fmtCur(summaryTotals.total)}</div></div>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr><th style={S.th}>Module</th><th style={S.thR}>Invoices</th><th style={S.thR}>Taxable</th><th style={S.thR}>CGST</th><th style={S.thR}>SGST</th><th style={S.thR}>IGST</th><th style={S.thR}>Total Tax</th><th style={S.thR}>Grand Total</th></tr>
          </thead>
          <tbody>
            {moduleSummary.map((r, i) => (
              <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
                <td style={S.td}><span style={S.badge(r.source_module === 'PMS' ? '#3498db' : r.source_module === 'KOT' ? '#e67e22' : r.source_module === 'BAR' ? '#9b59b6' : '#16a085')}>{r.source_module}</span></td>
                <td style={S.tdR}>{r.invoice_count}</td>
                <td style={S.tdR}>{fmt(r.taxable_amount)}</td>
                <td style={S.tdR}>{fmt(r.cgst_collected)}</td>
                <td style={S.tdR}>{fmt(r.sgst_collected)}</td>
                <td style={S.tdR}>{fmt(r.igst_collected)}</td>
                <td style={S.tdR}>{fmt(r.total_tax)}</td>
                <td style={S.tdR}><strong>{fmt(r.grand_total)}</strong></td>
              </tr>
            ))}
            {!moduleSummary.length && <tr><td colSpan={8} style={S.empty}>No data found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCustomerWise = () => (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>GSTIN</th><th style={S.th}>Customer</th><th style={S.thR}>Invoices</th>
            <th style={S.thR}>Taxable</th><th style={S.thR}>CGST</th><th style={S.thR}>SGST</th>
            <th style={S.thR}>IGST</th><th style={S.thR}>Total Value</th><th style={S.th}>Modules</th>
          </tr>
        </thead>
        <tbody>
          {customerWise.map((r, i) => (
            <tr key={i} style={i % 2 ? { background: '#f9fafb' } : {}}>
              <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '12px' }}>{r.customer_gstin}</td>
              <td style={S.td}>{r.customer_name || '-'}</td>
              <td style={S.tdR}>{r.invoice_count}</td>
              <td style={S.tdR}>{fmt(r.total_taxable)}</td>
              <td style={S.tdR}>{fmt(r.total_cgst)}</td>
              <td style={S.tdR}>{fmt(r.total_sgst)}</td>
              <td style={S.tdR}>{fmt(r.total_igst)}</td>
              <td style={S.tdR}><strong>{fmt(r.total_invoice_value)}</strong></td>
              <td style={S.td}>{r.modules}</td>
            </tr>
          ))}
          {!customerWise.length && <tr><td colSpan={9} style={S.empty}>No customer data found</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'filing': return renderFiling();
      case 'b2b': return renderB2B();
      case 'b2c-large': return renderB2CLarge();
      case 'b2c-small': return renderB2CSmall();
      case 'hsn': return renderHSN();
      case 'cdn': return renderCDN();
      case 'gstr1': return renderGSTR1();
      case 'gstr3b': return renderGSTR3B();
      case 'module': return renderModuleSummary();
      case 'customer': return renderCustomerWise();
      default: return null;
    }
  };

  return (
    <div style={S.container}>
      <div style={S.header}>
        <h2 style={S.title}>GST Reports</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={{ ...S.btn, ...S.btnSuccess }} onClick={() => exportCSV(getExportRows(), `gst_${activeTab}`)}>
            Export CSV
          </button>
          <button style={{ ...S.btn, background: '#8e44ad', color: '#fff' }} onClick={downloadPDF}>
            Print / PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={S.filtersRow}>
        <label style={{ fontSize: '13px', color: colors.textLight }}>From</label>
        <input type="date" value={dateRange.from} onChange={e => setDateRange(p => syncDateRange({ ...p, from: e.target.value }, 'from', 'to', 'from'))} style={S.input} />
        <label style={{ fontSize: '13px', color: colors.textLight }}>To</label>
        <input type="date" value={dateRange.to} min={dateRange.from || undefined} onChange={e => setDateRange(p => syncDateRange({ ...p, to: e.target.value }, 'from', 'to', 'to'))} style={S.input} />
        <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={S.select}>
          <option value="ALL">All Modules</option>
          <option value="PMS">PMS</option>
          <option value="KOT">KOT</option>
          <option value="BAR">BAR</option>
          <option value="BANQUET">Banquet</option>
        </select>
        {activeTab === 'b2b' && (
          <input placeholder="Search GSTIN..." value={gstinSearch} onChange={e => setGstinSearch(e.target.value.toUpperCase())} style={{ ...S.input, width: '180px' }} />
        )}
        <button style={{ ...S.btn, ...S.btnPrimary }} onClick={fetchTabData} disabled={loading}>
          {loading ? 'Loading...' : 'Load Report'}
        </button>
      </div>

      {/* Tab Bar */}
      <div style={S.tabBar}>
        {tabs.map(t => (
          <button key={t.key} style={S.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Error */}
      {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '12px', color: '#dc2626', marginBottom: '16px' }}>{error}</div>}

      {/* Content */}
      {loading ? (
        <div style={S.empty}>Loading...</div>
      ) : (
        renderContent()
      )}
    </div>
  );
}

export default GstReports;
